import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, useCallback } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Header } from '@/components/layout/Header'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useGroups } from '@/hooks/useGroups'
import { useDocumentHead } from '@/hooks/useDocumentHead'
import { leagueService } from '@/services/leagueService'
import { seasonService } from '@/services/seasonService'
import { bracketTemplateService } from '@/services/bracketTemplateService'
import { bracketService } from '@/services/bracketService'
import { bracketPlayerService } from '@/services/bracketPlayerService'
import { groupService } from '@/services/groupService'
import { matchService } from '@/services/matchService'
import { registrationService } from '@/services/registrationService'
import { ArrowLeft, AlertCircle, Loader2, Trophy, MoreHorizontal, Settings, User, UsersRound } from 'lucide-react'
import { ConfirmDeleteDialog } from '@/components/ui/confirm-delete-dialog'

// Import refactored components
import {
  LeagueSettingsDialog,
  SeasonFormDialog,
  AssignBracketDialog,
  SeasonsListView,
  SeasonBracketsView,
  BracketPlayersView,
  MatchesSection,
  BracketTemplatesSection,
  parseHashToNav,
  navToHash,
  type NavState,
  type League,
  type LeagueFormat,
  type MatchFormat,
  type Season,
  type JoiningFee,
  type BracketTemplate,
  type SeasonBracket,
  type BracketPlayer,
  type GroupMember,
  type AdminMatchSummary,
  type MatchStatus,
  type RegistrationWithProfile,
} from '@/components/league-admin'

export function LeagueAdmin() {
  const { groupId, leagueId } = useParams<{ groupId: string; leagueId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { groups, isLoading: groupsLoading } = useGroups()
  
  // Track where we came from for the back button
  const cameFrom = location.state?.from as string | undefined
  
  // Navigation state - initialized from URL hash
  const [nav, setNavState] = useState<NavState>(() => parseHashToNav(location.hash))
  
  // Wrapper to update both state and URL
  const setNav = useCallback((newNav: NavState) => {
    setNavState(newNav)
    const hash = navToHash(newNav)
    navigate({ hash: hash.slice(1) }, { replace: false })
  }, [navigate])
  
  // League data
  const [league, setLeague] = useState<League | null>(null)
  const [leagueLoading, setLeagueLoading] = useState(true)
  const [leagueError, setLeagueError] = useState<string | null>(null)
  
  // Settings
  const [showSettings, setShowSettings] = useState(false)
  const [settingsName, setSettingsName] = useState('')
  const [settingsDescription, setSettingsDescription] = useState('')
  const [settingsFormat, setSettingsFormat] = useState<LeagueFormat>('singles')
  const [settingsMatchFormat, setSettingsMatchFormat] = useState<MatchFormat>('bestOf1')
  const [settingsAutoApprove, setSettingsAutoApprove] = useState(false)
  const [settingsStatus, setSettingsStatus] = useState<'active' | 'archived'>('active')
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [deletingLeague, setDeletingLeague] = useState(false)
  
  // Seasons data
  const [seasons, setSeasons] = useState<Season[]>([])
  const [seasonsLoading, setSeasonsLoading] = useState(true)
  
  // Bracket templates
  const [templates, setTemplates] = useState<BracketTemplate[]>([])
  const [templatesLoading, setTemplatesLoading] = useState(true)
  
  // Season brackets
  const [seasonBrackets, setSeasonBrackets] = useState<Record<string, SeasonBracket[]>>({})
  
  // Season form state
  const [showSeasonForm, setShowSeasonForm] = useState(false)
  const [editingSeason, setEditingSeason] = useState<Season | null>(null)
  const [seasonName, setSeasonName] = useState('')
  const [seasonStart, setSeasonStart] = useState('')
  const [seasonEnd, setSeasonEnd] = useState('')
  const [seasonSignupsOpen, setSeasonSignupsOpen] = useState(false)
  const [seasonRegistrationMode, setSeasonRegistrationMode] = useState<'anyone' | 'group_members_only'>('group_members_only')
  const [seasonTerms, setSeasonTerms] = useState('')
  const [seasonHasFee, setSeasonHasFee] = useState(false)
  const [seasonFeeAmount, setSeasonFeeAmount] = useState('')
  const [seasonFeeCurrency, setSeasonFeeCurrency] = useState('GBP')
  const [seasonFeeDescription, setSeasonFeeDescription] = useState('')
  const [seasonHasGameLimit, setSeasonHasGameLimit] = useState(false)
  const [seasonMaxGamesPerOpponent, setSeasonMaxGamesPerOpponent] = useState(3)
  const [seasonShowBankDetails, setSeasonShowBankDetails] = useState(false)
  const [seasonPaymentReference, setSeasonPaymentReference] = useState('')
  const [seasonSaving, setSeasonSaving] = useState(false)
  const [seasonError, setSeasonError] = useState<string | null>(null)
  const [togglingSignups, setTogglingSignups] = useState<string | null>(null)
  
  // Template form state
  const [showTemplateForm, setShowTemplateForm] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<BracketTemplate | null>(null)
  const [templateName, setTemplateName] = useState('')
  const [templateDesc, setTemplateDesc] = useState('')
  const [draggingTemplateId, setDraggingTemplateId] = useState<string | null>(null)
  const [dragOverTemplateId, setDragOverTemplateId] = useState<string | null>(null)
  const [reorderingSaving, setReorderingSaving] = useState(false)
  const [templateSaving, setTemplateSaving] = useState(false)
  const [templateError, setTemplateError] = useState<string | null>(null)
  
  // Action states
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [togglingBracket, setTogglingBracket] = useState<string | null>(null)
  const [seasonActionsVisible, setSeasonActionsVisible] = useState(false)
  const [mainActionsVisible, setMainActionsVisible] = useState(false)
  const [bracketActionsVisible, setBracketActionsVisible] = useState(false)

  // Registrations state
  const [registrations, setRegistrations] = useState<RegistrationWithProfile[]>([])
  const [registrationsLoading, setRegistrationsLoading] = useState(false)
  const [processingRegistration, setProcessingRegistration] = useState<string | null>(null)
  const [assignBracketDialog, setAssignBracketDialog] = useState<{
    registration: RegistrationWithProfile
    selectedBracketId: string
  } | null>(null)
  
  // Players state
  const [players, setPlayers] = useState<BracketPlayer[]>([])
  const [playersLoading, setPlayersLoading] = useState(false)
  const [groupMembers, setGroupMembers] = useState<GroupMember[]>([])
  const [membersLoading, setMembersLoading] = useState(false)
  const [showAddPlayer, setShowAddPlayer] = useState(false)
  const [addingPlayerId, setAddingPlayerId] = useState<string | null>(null)
  const [removingPlayerId, setRemovingPlayerId] = useState<string | null>(null)
  const [playerSearch, setPlayerSearch] = useState('')
  
  // Matches state
  const [matches, setMatches] = useState<AdminMatchSummary[]>([])
  const [matchesLoading, setMatchesLoading] = useState(false)
  const [matchStatusFilter, setMatchStatusFilter] = useState<MatchStatus | 'all'>('all')
  const [approvingMatchId, setApprovingMatchId] = useState<string | null>(null)
  const [cancellingMatchId, setCancellingMatchId] = useState<string | null>(null)
  
  // Delete confirmation dialogs
  const [deleteLeagueDialogOpen, setDeleteLeagueDialogOpen] = useState(false)
  const [deleteLeagueError, setDeleteLeagueError] = useState<string | null>(null)
  const [deleteSeasonDialog, setDeleteSeasonDialog] = useState<Season | null>(null)
  const [deleteSeasonError, setDeleteSeasonError] = useState<string | null>(null)
  const [deleteTemplateDialog, setDeleteTemplateDialog] = useState<BracketTemplate | null>(null)
  const [deleteTemplateError, setDeleteTemplateError] = useState<string | null>(null)

  const group = groups.find(c => c.id === groupId)

  // Set page title
  useDocumentHead({
    title: league && group ? `Manage ${league.name} - ${group.name}` : 'League Admin',
    description: league ? `Admin settings for ${league.name}` : undefined,
  })

  // Sync nav state with URL hash (for browser back/forward)
  useEffect(() => {
    const newNav = parseHashToNav(location.hash)
    const currentHash = navToHash(nav)
    const newHash = navToHash(newNav)
    if (currentHash !== newHash) {
      setNavState(newNav)
    }
  }, [location.hash])

  // Animate actions sliding in when entering each view
  useEffect(() => {
    setMainActionsVisible(false)
    setSeasonActionsVisible(false)
    setBracketActionsVisible(false)
    
    const timer = setTimeout(() => {
      if (nav.view === 'main') setMainActionsVisible(true)
      else if (nav.view === 'season') setSeasonActionsVisible(true)
      else if (nav.view === 'bracket') setBracketActionsVisible(true)
    }, 50)
    
    return () => clearTimeout(timer)
  }, [nav.view, nav.view === 'season' ? nav.seasonId : null, nav.view === 'bracket' ? nav.bracketId : null])

  // Get current season and bracket from nav state
  const currentSeason = nav.view !== 'main' ? seasons.find(s => s.seasonId === nav.seasonId) : null
  const currentBracket = nav.view === 'bracket' 
    ? seasonBrackets[nav.seasonId]?.find(b => b.bracketId === nav.bracketId) 
    : null
  const currentTemplate = currentBracket 
    ? templates.find(t => t.templateId === currentBracket.templateId)
    : null

  // Load registrations when viewing season
  useEffect(() => {
    if (!leagueId || nav.view !== 'season' || !currentSeason) {
      setRegistrations([])
      return
    }

    if (currentSeason.status !== 'upcoming' || !currentSeason.signupsOpen) {
      setRegistrations([])
      return
    }

    async function loadRegistrations() {
      setRegistrationsLoading(true)
      try {
        const data = await registrationService.list(leagueId!, currentSeason!.seasonId)
        setRegistrations(data)
      } catch (err) {
        console.error('Failed to load registrations:', err)
        setRegistrations([])
      } finally {
        setRegistrationsLoading(false)
      }
    }

    loadRegistrations()
  }, [leagueId, nav.view, currentSeason?.seasonId, currentSeason?.status, currentSeason?.signupsOpen])

  // Load league
  useEffect(() => {
    if (!groupId || !leagueId) return
    
    async function loadLeague() {
      setLeagueLoading(true)
      setLeagueError(null)
      try {
        const data = await leagueService.get(groupId!, leagueId!)
        setLeague(data)
        setSettingsName(data.name)
        setSettingsDescription(data.description || '')
        setSettingsFormat(data.format || 'singles')
        setSettingsMatchFormat(data.matchFormat || 'bestOf1')
        setSettingsAutoApprove(data.autoApprove || false)
        setSettingsStatus(data.status)
      } catch (err) {
        console.error('Failed to load league:', err)
        setLeagueError('Failed to load league')
      } finally {
        setLeagueLoading(false)
      }
    }
    
    loadLeague()
  }, [groupId, leagueId])

  // Load seasons and templates
  useEffect(() => {
    if (!groupId || !leagueId) return
    
    async function loadData() {
      setSeasonsLoading(true)
      setTemplatesLoading(true)
      try {
        const [seasonsData, templatesData] = await Promise.all([
          seasonService.list(leagueId!),
          bracketTemplateService.list(leagueId!),
        ])
        setSeasons(seasonsData)
        setTemplates(templatesData)
        
        const bracketsMap: Record<string, SeasonBracket[]> = {}
        await Promise.all(
          seasonsData.map(async (season) => {
            try {
              const brackets = await bracketService.list(leagueId!, season.seasonId)
              bracketsMap[season.seasonId] = brackets
            } catch {
              bracketsMap[season.seasonId] = []
            }
          })
        )
        setSeasonBrackets(bracketsMap)
      } catch (err) {
        console.error('Failed to load data:', err)
      } finally {
        setSeasonsLoading(false)
        setTemplatesLoading(false)
      }
    }
    
    loadData()
  }, [groupId, leagueId])

  // Load players when viewing a bracket
  useEffect(() => {
    if (nav.view !== 'bracket' || !groupId || !leagueId) return
    
    const { bracketId } = nav
    
    async function loadPlayers() {
      setPlayersLoading(true)
      try {
        const data = await bracketPlayerService.list(bracketId)
        setPlayers(data)
      } catch (err) {
        console.error('Failed to load players:', err)
        setPlayers([])
      } finally {
        setPlayersLoading(false)
      }
    }
    
    loadPlayers()
  }, [nav, groupId, leagueId])

  // Load group members when showing add player dialog
  useEffect(() => {
    if (!showAddPlayer || !groupId) return
    
    async function loadMembers() {
      setMembersLoading(true)
      try {
        const data = await groupService.listMembers(groupId!)
        setGroupMembers(data)
      } catch (err) {
        console.error('Failed to load members:', err)
        setGroupMembers([])
      } finally {
        setMembersLoading(false)
      }
    }
    
    loadMembers()
  }, [showAddPlayer, groupId])
  
  // Load matches for group
  useEffect(() => {
    if (!groupId) return
    
    async function loadMatches() {
      setMatchesLoading(true)
      try {
        const result = await matchService.listGroupMatches(groupId!, {
          status: matchStatusFilter === 'all' ? undefined : matchStatusFilter,
          limit: 50,
        })
        setMatches(result.data)
      } catch (err) {
        console.error('Failed to load matches:', err)
        setMatches([])
      } finally {
        setMatchesLoading(false)
      }
    }
    
    loadMatches()
  }, [groupId, matchStatusFilter])

  // Settings handlers
  const handleSaveSettings = async () => {
    if (!groupId || !leagueId || !settingsName.trim()) return
    
    setSettingsSaving(true)
    setSettingsError(null)
    
    try {
      const updated = await leagueService.update(groupId, leagueId, {
        name: settingsName.trim(),
        description: settingsDescription.trim() || undefined,
        format: settingsFormat,
        matchFormat: settingsMatchFormat,
        autoApprove: settingsAutoApprove,
        status: settingsStatus,
      })
      setLeague(updated)
      setShowSettings(false)
    } catch (err) {
      setSettingsError((err as Error)?.message || 'Failed to save')
    } finally {
      setSettingsSaving(false)
    }
  }

  const handleDeleteLeague = async () => {
    if (!groupId || !leagueId || !league) return
    
    setDeletingLeague(true)
    setDeleteLeagueError(null)
    try {
      await leagueService.delete(groupId, leagueId)
      setDeleteLeagueDialogOpen(false)
      navigate(`/group/${groupId}/admin#leagues`)
    } catch (err) {
      setDeleteLeagueError((err as Error)?.message || 'Failed to delete league')
      setDeletingLeague(false)
    }
  }

  // Season handlers
  const resetSeasonForm = () => {
    setEditingSeason(null)
    setSeasonName('')
    setSeasonStart('')
    setSeasonEnd('')
    setSeasonSignupsOpen(false)
    setSeasonRegistrationMode('group_members_only')
    setSeasonTerms('')
    setSeasonHasFee(false)
    setSeasonFeeAmount('')
    setSeasonFeeCurrency('GBP')
    setSeasonFeeDescription('')
    setSeasonHasGameLimit(false)
    setSeasonMaxGamesPerOpponent(3)
    setSeasonShowBankDetails(false)
    setSeasonPaymentReference('')
    setSeasonError(null)
    setShowSeasonForm(false)
  }

  const openSeasonForm = (season?: Season) => {
    if (season) {
      setEditingSeason(season)
      setSeasonName(season.name)
      setSeasonStart(season.startDate)
      setSeasonEnd(season.endDate)
      setSeasonSignupsOpen(season.signupsOpen || false)
      setSeasonRegistrationMode(season.registrationMode || 'group_members_only')
      setSeasonTerms(season.termsAndConditions || '')
      if (season.joiningFee) {
        setSeasonHasFee(true)
        setSeasonFeeAmount((season.joiningFee.amount / 100).toFixed(2))
        setSeasonFeeCurrency(season.joiningFee.currency)
        setSeasonFeeDescription(season.joiningFee.description || '')
      } else {
        setSeasonHasFee(false)
        setSeasonFeeAmount('')
        setSeasonFeeCurrency('GBP')
        setSeasonFeeDescription('')
      }
      setSeasonHasGameLimit(season.maxGamesPerOpponent != null && season.maxGamesPerOpponent > 0)
      setSeasonMaxGamesPerOpponent(season.maxGamesPerOpponent || 3)
      setSeasonShowBankDetails(season.showBankDetails || false)
      setSeasonPaymentReference(season.paymentReference || '')
    } else {
      resetSeasonForm()
    }
    setShowSeasonForm(true)
  }

  const handleSaveSeason = async () => {
    if (!groupId || !leagueId) return
    if (!seasonName.trim() || !seasonStart || !seasonEnd) {
      setSeasonError('All fields are required')
      return
    }
    if (seasonStart > seasonEnd) {
      setSeasonError('Start date must be before end date')
      return
    }
    
    let joiningFee: JoiningFee | null | undefined = undefined
    if (seasonHasFee) {
      const feeAmount = parseFloat(seasonFeeAmount)
      if (isNaN(feeAmount) || feeAmount <= 0) {
        setSeasonError('Please enter a valid fee amount')
        return
      }
      joiningFee = {
        amount: Math.round(feeAmount * 100),
        currency: seasonFeeCurrency,
        description: seasonFeeDescription.trim() || undefined,
      }
    } else if (editingSeason?.joiningFee) {
      joiningFee = null
    }

    setSeasonSaving(true)
    setSeasonError(null)
    
    try {
      if (editingSeason) {
        await seasonService.update(leagueId, editingSeason.seasonId, {
          name: seasonName.trim(),
          startDate: seasonStart,
          endDate: seasonEnd,
          signupsOpen: seasonSignupsOpen,
          registrationMode: seasonRegistrationMode,
          termsAndConditions: seasonTerms.trim() || null,
          joiningFee,
          showBankDetails: seasonHasFee ? seasonShowBankDetails : false,
          paymentReference: (seasonHasFee && seasonShowBankDetails && seasonPaymentReference.trim()) 
            ? seasonPaymentReference.trim() 
            : null,
          maxGamesPerOpponent: seasonHasGameLimit ? seasonMaxGamesPerOpponent : null,
        })
      } else {
        await seasonService.create(leagueId, {
          name: seasonName.trim(),
          startDate: seasonStart,
          endDate: seasonEnd,
          signupsOpen: seasonSignupsOpen,
          registrationMode: seasonRegistrationMode,
          termsAndConditions: seasonTerms.trim() || undefined,
          joiningFee: joiningFee ?? undefined,
          showBankDetails: seasonHasFee ? seasonShowBankDetails : false,
          paymentReference: (seasonHasFee && seasonShowBankDetails && seasonPaymentReference.trim()) 
            ? seasonPaymentReference.trim() 
            : undefined,
          maxGamesPerOpponent: seasonHasGameLimit ? seasonMaxGamesPerOpponent : null,
        })
      }
      const updated = await seasonService.list(leagueId)
      setSeasons(updated)
      resetSeasonForm()
    } catch (err) {
      setSeasonError((err as Error)?.message || 'Failed to save')
    } finally {
      setSeasonSaving(false)
    }
  }

  const handleToggleSignups = async (season: Season) => {
    if (!leagueId) return

    setTogglingSignups(season.seasonId)
    try {
      await seasonService.update(leagueId, season.seasonId, {
        signupsOpen: !season.signupsOpen,
      })
      setSeasons(prev => prev.map(s =>
        s.seasonId === season.seasonId
          ? { ...s, signupsOpen: !s.signupsOpen }
          : s
      ))
    } catch {
      alert('Failed to update signups status')
    } finally {
      setTogglingSignups(null)
    }
  }

  // Registration handlers
  const handleOpenAssignBracket = (registration: RegistrationWithProfile) => {
    const brackets = nav.view === 'season' ? seasonBrackets[nav.seasonId] || [] : []
    setAssignBracketDialog({
      registration,
      selectedBracketId: registration.preferredBracketId || (brackets[0]?.bracketId || ''),
    })
  }

  const handleConfirmRegistration = async () => {
    if (!leagueId || !assignBracketDialog) return

    setProcessingRegistration(assignBracketDialog.registration.registrationId)
    try {
      await registrationService.confirm(
        leagueId,
        assignBracketDialog.registration.seasonId,
        assignBracketDialog.registration.registrationId,
        assignBracketDialog.selectedBracketId
      )
      setRegistrations(prev => prev.map(r =>
        r.registrationId === assignBracketDialog.registration.registrationId
          ? { ...r, status: 'confirmed', assignedBracketId: assignBracketDialog.selectedBracketId }
          : r
      ))
      setAssignBracketDialog(null)
      if (nav.view === 'season') {
        const brackets = await bracketService.list(leagueId, nav.seasonId)
        setSeasonBrackets(prev => ({ ...prev, [nav.seasonId]: brackets }))
      }
    } catch (err) {
      alert((err as Error)?.message || 'Failed to confirm registration')
    } finally {
      setProcessingRegistration(null)
    }
  }

  const handleRejectRegistration = async (registration: RegistrationWithProfile) => {
    if (!leagueId) return

    const confirmed = window.confirm(`Are you sure you want to reject ${registration.profile.displayName}'s registration?`)
    if (!confirmed) return

    setProcessingRegistration(registration.registrationId)
    try {
      await registrationService.reject(leagueId, registration.seasonId, registration.registrationId)
      setRegistrations(prev => prev.map(r =>
        r.registrationId === registration.registrationId
          ? { ...r, status: 'rejected' }
          : r
      ))
    } catch (err) {
      alert((err as Error)?.message || 'Failed to reject registration')
    } finally {
      setProcessingRegistration(null)
    }
  }

  const handleDeleteSeason = async () => {
    if (!groupId || !leagueId || !deleteSeasonDialog) return
    
    const season = deleteSeasonDialog
    setDeletingId(season.seasonId)
    setDeleteSeasonError(null)
    try {
      await seasonService.delete(leagueId, season.seasonId)
      setSeasons(prev => prev.filter(s => s.seasonId !== season.seasonId))
      setDeleteSeasonDialog(null)
      if (nav.view !== 'main' && nav.seasonId === season.seasonId) {
        setNav({ view: 'main' })
      }
    } catch (err) {
      setDeleteSeasonError((err as Error)?.message || 'Failed to delete season')
    } finally {
      setDeletingId(null)
    }
  }

  // Template handlers
  const resetTemplateForm = () => {
    setEditingTemplate(null)
    setTemplateName('')
    setTemplateDesc('')
    setTemplateError(null)
    setShowTemplateForm(false)
  }

  const openTemplateForm = (template?: BracketTemplate) => {
    if (template) {
      setEditingTemplate(template)
      setTemplateName(template.name)
      setTemplateDesc(template.description || '')
    } else {
      resetTemplateForm()
    }
    setShowTemplateForm(true)
  }

  const handleSaveTemplate = async () => {
    if (!groupId || !leagueId) return
    if (!templateName.trim()) {
      setTemplateError('Name is required')
      return
    }

    setTemplateSaving(true)
    setTemplateError(null)
    
    try {
      if (editingTemplate) {
        await bracketTemplateService.update(leagueId, editingTemplate.templateId, {
          name: templateName.trim(),
          description: templateDesc.trim() || undefined,
        })
      } else {
        await bracketTemplateService.create(leagueId, {
          name: templateName.trim(),
          description: templateDesc.trim() || undefined,
        })
      }
      const updated = await bracketTemplateService.list(leagueId)
      setTemplates(updated)
      resetTemplateForm()
    } catch (err) {
      setTemplateError((err as Error)?.message || 'Failed to save')
    } finally {
      setTemplateSaving(false)
    }
  }

  const handleDeleteTemplate = async () => {
    if (!groupId || !leagueId || !deleteTemplateDialog) return

    const template = deleteTemplateDialog
    setDeletingId(template.templateId)
    setDeleteTemplateError(null)
    try {
      await bracketTemplateService.delete(leagueId, template.templateId)
      setTemplates(prev => prev.filter(t => t.templateId !== template.templateId))
      setDeleteTemplateDialog(null)
    } catch (err) {
      setDeleteTemplateError((err as Error)?.message || 'Failed to delete bracket type')
    } finally {
      setDeletingId(null)
    }
  }

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, templateId: string) => {
    setDraggingTemplateId(templateId)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', templateId)
  }

  const handleDragOver = (e: React.DragEvent, templateId: string) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (templateId !== draggingTemplateId) {
      setDragOverTemplateId(templateId)
    }
  }

  const handleDragLeave = () => {
    setDragOverTemplateId(null)
  }

  const handleDragEnd = () => {
    setDraggingTemplateId(null)
    setDragOverTemplateId(null)
  }

  const handleDrop = async (e: React.DragEvent, targetTemplateId: string) => {
    e.preventDefault()
    
    if (!leagueId || !draggingTemplateId || draggingTemplateId === targetTemplateId) {
      handleDragEnd()
      return
    }

    const draggedIndex = templates.findIndex(t => t.templateId === draggingTemplateId)
    const targetIndex = templates.findIndex(t => t.templateId === targetTemplateId)
    
    if (draggedIndex === -1 || targetIndex === -1) {
      handleDragEnd()
      return
    }

    const newTemplates = [...templates]
    const [draggedItem] = newTemplates.splice(draggedIndex, 1)
    newTemplates.splice(targetIndex, 0, draggedItem)
    setTemplates(newTemplates)
    handleDragEnd()

    setReorderingSaving(true)
    try {
      const templateIds = newTemplates.map(t => t.templateId)
      await bracketTemplateService.reorder(leagueId, templateIds)
    } catch {
      setTemplates(templates)
      alert('Failed to save order')
    } finally {
      setReorderingSaving(false)
    }
  }

  // Bracket toggle
  const handleToggleBracket = async (seasonId: string, template: BracketTemplate, enable: boolean) => {
    if (!groupId || !leagueId) return
    
    setTogglingBracket(template.templateId)
    try {
      if (enable) {
        await bracketService.create(leagueId, seasonId, { templateId: template.templateId })
      } else {
        const bracket = seasonBrackets[seasonId]?.find(b => b.templateId === template.templateId)
        if (bracket) {
          await bracketService.delete(leagueId, seasonId, bracket.bracketId)
        }
      }
      const updated = await bracketService.list(leagueId, seasonId)
      setSeasonBrackets(prev => ({ ...prev, [seasonId]: updated }))
    } catch {
      alert('Failed to update bracket')
    } finally {
      setTogglingBracket(null)
    }
  }

  const isTemplateActive = (seasonId: string, templateId: string) => {
    return seasonBrackets[seasonId]?.some(b => b.templateId === templateId) || false
  }

  const getBracketCount = (seasonId: string) => {
    return seasonBrackets[seasonId]?.length || 0
  }

  // Player handlers
  const handleAddPlayer = async (userId: string) => {
    if (!groupId || !leagueId || nav.view !== 'bracket') return
    
    setAddingPlayerId(userId)
    try {
      const newPlayer = await bracketPlayerService.add(nav.bracketId, userId)
      setPlayers(prev => [...prev, newPlayer])
      setShowAddPlayer(false)
      setPlayerSearch('')
    } catch (err) {
      alert((err as Error)?.message || 'Failed to add player')
    } finally {
      setAddingPlayerId(null)
    }
  }

  const handleRemovePlayer = async (userId: string) => {
    if (!groupId || !leagueId || nav.view !== 'bracket') return
    if (!confirm('Remove this player from the bracket?')) return
    
    setRemovingPlayerId(userId)
    try {
      await bracketPlayerService.remove(nav.bracketId, userId)
      setPlayers(prev => prev.filter(p => p.userId !== userId))
    } catch {
      alert('Failed to remove player')
    } finally {
      setRemovingPlayerId(null)
    }
  }
  
  // Match handlers
  const handleApproveMatch = async (match: AdminMatchSummary) => {
    if (!groupId) return
    
    setApprovingMatchId(match.matchId)
    try {
      await matchService.approve(match.bracketId, match.matchId)
      setMatches(prev => prev.map(m => 
        m.matchId === match.matchId 
          ? { ...m, status: 'approved' as MatchStatus, approvedAt: new Date().toISOString() }
          : m
      ))
    } catch (err) {
      alert((err as Error)?.message || 'Failed to approve match')
    } finally {
      setApprovingMatchId(null)
    }
  }
  
  const handleCancelMatch = async (match: AdminMatchSummary) => {
    if (!groupId) return
    
    if (!confirm('Are you sure you want to delete this match? This action cannot be undone.')) {
      return
    }
    
    setCancellingMatchId(match.matchId)
    try {
      await matchService.cancel(match.bracketId, match.matchId)
      setMatches(prev => prev.filter(m => m.matchId !== match.matchId))
    } catch (err) {
      alert((err as Error)?.message || 'Failed to delete match')
    } finally {
      setCancellingMatchId(null)
    }
  }

  // Filter members not already in bracket
  const availableMembers = groupMembers.filter(
    m => !players.some(p => p.userId === m.userId)
  ).filter(
    m => playerSearch ? m.displayName.toLowerCase().includes(playerSearch.toLowerCase()) : true
  )

  // Loading state
  if (groupsLoading || leagueLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </main>
      </div>
    )
  }

  // Error state
  if (!group || !group.isAdmin || leagueError || !league) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <Card className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">
              {leagueError || 'Access Denied'}
            </h2>
            <Button onClick={() => navigate(`/group/${groupId}/admin#leagues`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Group Admin
            </Button>
          </Card>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => {
            if (cameFrom === 'league-view') {
              navigate(`/group/${groupId}/league/${leagueId}`)
            } else {
              navigate(`/group/${groupId}/admin#leagues`)
            }
          }} 
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {cameFrom === 'league-view' ? `Back to ${league?.name || 'League'}` : `Back to ${group.name}`}
        </Button>

        {/* League Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className={`h-12 w-12 sm:h-14 sm:w-14 rounded-xl flex items-center justify-center shrink-0 ${
              league.status === 'active' ? 'bg-green-500/10' : 'bg-gray-500/10'
            }`}>
              <Trophy className={`h-6 w-6 sm:h-7 sm:w-7 ${
                league.status === 'active' ? 'text-green-600 dark:text-green-400' : 'text-gray-500'
              }`} />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-bold flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="truncate">{league.name}</span>
                <span className={`inline-flex items-center gap-1 text-xs sm:text-sm font-medium px-2 sm:px-2.5 py-0.5 rounded-full shrink-0 ${
                  league.format === 'singles'
                    ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                }`}>
                  {league.format === 'singles' ? <User className="h-3 w-3 sm:h-3.5 sm:w-3.5" /> : <UsersRound className="h-3 w-3 sm:h-3.5 sm:w-3.5" />}
                  {league.format === 'singles' ? 'Singles' : 'Doubles'}
                </span>
              </h1>
              <p className="text-sm text-muted-foreground line-clamp-2">{league.description || 'No description'}</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9 shrink-0 self-start">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowSettings(true)}>
                <Settings className="h-4 w-4 mr-2" />
                League Settings
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Main Content Grid */}
        <div className="grid gap-6 md:grid-cols-3">
          {/* Left: Navigation + Matches */}
          <div className="md:col-span-2 space-y-4">
            {/* Main View: Seasons List */}
            {nav.view === 'main' && (
              <SeasonsListView
                seasons={seasons}
                seasonsLoading={seasonsLoading}
                getBracketCount={getBracketCount}
                onSeasonClick={(seasonId) => setNav({ view: 'season', seasonId })}
                onNewSeason={() => openSeasonForm()}
                actionsVisible={mainActionsVisible}
              />
            )}

            {/* Season View: Brackets List */}
            {nav.view === 'season' && currentSeason && (
              <SeasonBracketsView
                season={currentSeason}
                templates={templates}
                seasonBrackets={seasonBrackets[currentSeason.seasonId] || []}
                isTemplateActive={(templateId) => isTemplateActive(currentSeason.seasonId, templateId)}
                togglingBracket={togglingBracket}
                onToggleBracket={(template, enable) => handleToggleBracket(currentSeason.seasonId, template, enable)}
                onBracketClick={(bracketId) => setNav({ view: 'bracket', seasonId: currentSeason.seasonId, bracketId })}
                onBackToSeasons={() => setNav({ view: 'main' })}
                onEditSeason={() => openSeasonForm(currentSeason)}
                onDeleteSeason={() => setDeleteSeasonDialog(currentSeason)}
                deletingId={deletingId}
                togglingSignups={togglingSignups}
                onToggleSignups={() => handleToggleSignups(currentSeason)}
                registrations={registrations}
                registrationsLoading={registrationsLoading}
                processingRegistration={processingRegistration}
                onApproveRegistration={handleOpenAssignBracket}
                onRejectRegistration={handleRejectRegistration}
                actionsVisible={seasonActionsVisible}
              />
            )}

            {/* Bracket View: Players */}
            {nav.view === 'bracket' && currentBracket && currentTemplate && currentSeason && (
              <BracketPlayersView
                season={currentSeason}
                template={currentTemplate}
                players={players}
                playersLoading={playersLoading}
                showAddPlayer={showAddPlayer}
                onShowAddPlayer={setShowAddPlayer}
                playerSearch={playerSearch}
                onPlayerSearchChange={setPlayerSearch}
                availableMembers={availableMembers}
                membersLoading={membersLoading}
                addingPlayerId={addingPlayerId}
                onAddPlayer={handleAddPlayer}
                removingPlayerId={removingPlayerId}
                onRemovePlayer={handleRemovePlayer}
                onBackToSeasons={() => setNav({ view: 'main' })}
                onBackToSeason={() => setNav({ view: 'season', seasonId: currentSeason.seasonId })}
                actionsVisible={bracketActionsVisible}
              />
            )}

            {/* Matches Section */}
            <MatchesSection
              matches={matches}
              matchesLoading={matchesLoading}
              matchStatusFilter={matchStatusFilter}
              onMatchStatusFilterChange={setMatchStatusFilter}
              seasons={seasons}
              seasonBrackets={seasonBrackets}
              templates={templates}
              approvingMatchId={approvingMatchId}
              cancellingMatchId={cancellingMatchId}
              onApproveMatch={handleApproveMatch}
              onCancelMatch={handleCancelMatch}
            />
          </div>

          {/* Right Sidebar */}
          <div className="space-y-4">
            <BracketTemplatesSection
              templates={templates}
              templatesLoading={templatesLoading}
              showForm={showTemplateForm}
              editingTemplate={editingTemplate}
              name={templateName}
              onNameChange={setTemplateName}
              description={templateDesc}
              onDescriptionChange={setTemplateDesc}
              onOpenForm={openTemplateForm}
              onCloseForm={resetTemplateForm}
              onSave={handleSaveTemplate}
              onDelete={(template) => setDeleteTemplateDialog(template)}
              saving={templateSaving}
              deletingId={deletingId}
              error={templateError}
              draggingTemplateId={draggingTemplateId}
              dragOverTemplateId={dragOverTemplateId}
              reorderingSaving={reorderingSaving}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDragEnd={handleDragEnd}
              onDrop={handleDrop}
            />
          </div>
        </div>

        {/* Dialogs */}
        <LeagueSettingsDialog
          open={showSettings}
          onOpenChange={setShowSettings}
          league={league}
          name={settingsName}
          onNameChange={setSettingsName}
          description={settingsDescription}
          onDescriptionChange={setSettingsDescription}
          format={settingsFormat}
          onFormatChange={setSettingsFormat}
          matchFormat={settingsMatchFormat}
          onMatchFormatChange={setSettingsMatchFormat}
          autoApprove={settingsAutoApprove}
          onAutoApproveChange={setSettingsAutoApprove}
          status={settingsStatus}
          onStatusChange={setSettingsStatus}
          onSave={handleSaveSettings}
          onDelete={() => setDeleteLeagueDialogOpen(true)}
          saving={settingsSaving}
          deleting={deletingLeague}
          error={settingsError}
        />

        <SeasonFormDialog
          open={showSeasonForm}
          onOpenChange={setShowSeasonForm}
          editingSeason={editingSeason}
          name={seasonName}
          onNameChange={setSeasonName}
          startDate={seasonStart}
          onStartDateChange={setSeasonStart}
          endDate={seasonEnd}
          onEndDateChange={setSeasonEnd}
          signupsOpen={seasonSignupsOpen}
          onSignupsOpenChange={setSeasonSignupsOpen}
          registrationMode={seasonRegistrationMode}
          onRegistrationModeChange={setSeasonRegistrationMode}
          terms={seasonTerms}
          onTermsChange={setSeasonTerms}
          hasFee={seasonHasFee}
          onHasFeeChange={setSeasonHasFee}
          feeAmount={seasonFeeAmount}
          onFeeAmountChange={setSeasonFeeAmount}
          feeCurrency={seasonFeeCurrency}
          onFeeCurrencyChange={setSeasonFeeCurrency}
          feeDescription={seasonFeeDescription}
          onFeeDescriptionChange={setSeasonFeeDescription}
          hasGameLimit={seasonHasGameLimit}
          onHasGameLimitChange={setSeasonHasGameLimit}
          maxGamesPerOpponent={seasonMaxGamesPerOpponent}
          onMaxGamesPerOpponentChange={setSeasonMaxGamesPerOpponent}
          showBankDetails={seasonShowBankDetails}
          onShowBankDetailsChange={setSeasonShowBankDetails}
          hasBankDetails={!!(group?.bankDetails && Object.values(group.bankDetails).some(v => v && typeof v === 'string' && v.trim()))}
          paymentReference={seasonPaymentReference}
          onPaymentReferenceChange={setSeasonPaymentReference}
          groupDefaultReference={group?.bankDetails?.reference}
          onSave={handleSaveSeason}
          onCancel={resetSeasonForm}
          saving={seasonSaving}
          error={seasonError}
        />

        <AssignBracketDialog
          open={!!assignBracketDialog}
          onOpenChange={(open) => { if (!open) setAssignBracketDialog(null) }}
          registration={assignBracketDialog?.registration || null}
          brackets={nav.view === 'season' ? seasonBrackets[nav.seasonId] || [] : []}
          selectedBracketId={assignBracketDialog?.selectedBracketId || ''}
          onBracketSelect={(bracketId) => setAssignBracketDialog(prev => 
            prev ? { ...prev, selectedBracketId: bracketId } : null
          )}
          onConfirm={handleConfirmRegistration}
          processing={!!processingRegistration}
        />

        {/* Delete League Confirmation */}
        {league && (
          <ConfirmDeleteDialog
            open={deleteLeagueDialogOpen}
            onOpenChange={(open) => {
              setDeleteLeagueDialogOpen(open)
              if (!open) setDeleteLeagueError(null)
            }}
            title="Delete League"
            description="This action is permanent and cannot be undone."
            confirmText={league.name}
            consequences={[
              'All seasons and their configurations',
              'All brackets and player assignments',
              'All matches and scores',
              'All registration history',
            ]}
            onConfirm={handleDeleteLeague}
            loading={deletingLeague}
            error={deleteLeagueError}
          />
        )}

        {/* Delete Season Confirmation */}
        {deleteSeasonDialog && (
          <ConfirmDeleteDialog
            open={!!deleteSeasonDialog}
            onOpenChange={(open) => {
              if (!open) {
                setDeleteSeasonDialog(null)
                setDeleteSeasonError(null)
              }
            }}
            title="Delete Season"
            description="This action is permanent and cannot be undone."
            confirmText={deleteSeasonDialog.name}
            consequences={[
              'All brackets in this season',
              'All player assignments',
              'All matches and scores',
            ]}
            onConfirm={handleDeleteSeason}
            loading={deletingId === deleteSeasonDialog.seasonId}
            error={deleteSeasonError}
          />
        )}

        {/* Delete Template Confirmation */}
        {deleteTemplateDialog && (
          <ConfirmDeleteDialog
            open={!!deleteTemplateDialog}
            onOpenChange={(open) => {
              if (!open) {
                setDeleteTemplateDialog(null)
                setDeleteTemplateError(null)
              }
            }}
            title="Delete Bracket Type"
            description="This bracket type will be removed from the league."
            confirmText={deleteTemplateDialog.name}
            onConfirm={handleDeleteTemplate}
            loading={deletingId === deleteTemplateDialog.templateId}
            error={deleteTemplateError}
          />
        )}
      </main>
    </div>
  )
}
